import padStart from 'lodash/padStart';

import { ONE_DAY, ONE_HOUR, ONE_MINUTE, ONE_SECOND } from 'Lib/constants/time';

export type DateShape = { day: number; month: number; year: number };

export function millisecondsToAge(seconds: number) {
  const ageDate = new Date(seconds); // milliseconds from epoch
  return Math.abs(ageDate.getUTCFullYear() - 1970);
}

// convert Date or milliseconds to Epoch Time
export function dateToEpochSeconds(date: Date | number | string) {
  const milliseconds = new Date(date).getTime();
  return Math.floor(milliseconds / ONE_SECOND);
}

// http://stackoverflow.com/questions/4060004/calculate-age-in-javascript
export function calculateAge(birthday: Date | number | string = new Date()) {
  // birthday is a date
  const bDay = new Date(birthday);
  const ageDifMs = Date.now() - bDay.getTime();
  return millisecondsToAge(ageDifMs); // miliseconds from epoch
}

export function formatRemainingTime(
  time: number,
  showHour: boolean = true,
  showMinute: boolean = true
): string {
  if (!time || isNaN(time) || time < 0) {
    if (showHour) {
      return '0:00:00';
    }
    if (showMinute) {
      return '00:00';
    }
    return '0';
  }

  const hours: number = Math.floor(time / ONE_HOUR);
  const minutes: number = Math.floor((time % ONE_HOUR) / ONE_MINUTE);
  const seconds: number = Math.floor((time % ONE_MINUTE) / ONE_SECOND);

  const displayTime = [padStart(`${seconds}`, 2, '0')];
  if (showMinute || showHour) {
    displayTime.unshift(padStart(`${minutes}`, 2, '0'));
  }
  if (showHour) {
    displayTime.unshift(padStart(`${hours}`, 2, '0'));
  }

  if (!showMinute && !showHour) {
    return `${seconds}`;
  }
  return displayTime.join(':');
}

export function getCurrentDayMonthYear(): {
  day: number;
  month: number;
  year: number;
} {
  const today = new Date();
  return { day: today.getDate(), month: today.getMonth() + 1, year: today.getFullYear() };
}

/**
 * Returns the number of days left until X number of years from now
 *
 * @param  {Date} start - Relative time as a starting point
 * @param  {number} yearsUntilThen - number of years into the future
 * @return {number} Number of days
 */
export function getDaysLeft(start: Date, yearsUntilThen: number | null) {
  if (!yearsUntilThen) {
    return null;
  }
  const daysLeft = Math.ceil(
    (start.setFullYear(start.getFullYear() + yearsUntilThen) - Date.now()) / ONE_DAY
  );
  return daysLeft;
}

export function getDateFromTimeRemaining(timeRemainingMs: number = 0, yearsOffset: number = 18) {
  const now = new Date(Date.now()); // Use Date.now() so we can mock in tests
  const nowWithOffset = new Date(now.setFullYear(now.getFullYear() - yearsOffset));
  return new Date(nowWithOffset.valueOf() + timeRemainingMs);
}

/**
 * Get the number of days in any particular month
 * Credit: https://www.w3resource.com/javascript-exercises/javascript-date-exercise-3.php
 * @return {number} The number of days in the month
 */
function daysInMonth({ month, year }: { month: number; year: number }) {
  return new Date(year, month, 0).getDate();
}

/**
 * Check if a date is valid
 * Credit: https://stackoverflow.com/a/1433119/1293256
 */
export function isValidDate({ day, month, year }: DateShape) {
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth({ month, year });
}

export function isBeforeToday({ day, month, year }: DateShape) {
  const today = new Date();
  const inputDate = new Date(year, month - 1, day);
  return inputDate < today;
}
